/** @format */

import React, { useEffect, useState } from "react";
import CreateResourceForm, {
  ResourceForm,
} from "../createResourceForm/CreateResourceForm";
import { Button, Alert } from "react-bootstrap";
import AuthFlag from "../../auth/AuthFlag";
import { ReactComponent as ExclamationTriangle } from "bootstrap-icons/icons/exclamation-triangle.svg";
import { useCreateResourceReferenceMutation } from "../../../app/services/resourcesApi";
import { useLocale } from "../../../i18n/hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import ResourceLinks from "../resourceLinks/ResourceLinks";
import { useTranslation } from "react-i18next";
import {
  QualificationId,
  ResourceLink,
  ResourceReference,
  ResourceType,
  Subject,
} from "../../../types";
import { Link } from "react-router-dom";
import { Locale } from "../../../i18n/init";

enum stageEnum {
  StageOne = "StageOne",
  StageTwo = "StageTwo",
  StageThree = "StageThree",
  StageFour = "StageFour",
}

/**
 * @returns The default description for the given resource type.
 *  Returns an empty string if the resource type doesn't have a default description
 */
const getDefaultResourceDescription = (
  locale: Locale,
  resourceType: ResourceType,
  title?: string,
): string => {
  //TODO: add descriptions for CR, DT, QB, and OR
  if (locale === "en") {
    switch (resourceType) {
      case "KO":
        return (
          "A bank of sample knowledge organisers to support the learning of " +
          title +
          ". These could be used for revision or as a starting point for creating your own knowledge organisers. "
        );
      case "SP":
        return "A downloadable planner to aid learners in tracking their understanding of the topics to be assessed. This will be an invaluable resource to aid revision by giving an instant overview of where gaps in knowledge need to be addressed.";
      case "CR":
        return "";
      case "DT":
        return "";
      case "QB":
        return "Build your own papers to test your understanding of certain topics. The question bank also contains mark schemes so that you can mark your own work and check your progress.";
      case "OER":
        return "Check answers to past paper questions and consider the examiners comments. These resources will help you understand what the questions are asking for and help you avoid common pitfalls.";
      case "EWT":
        return "Aimed at learners, this Exam Walk Through resource offers practical hints and tips on how to effectively approach questions in the examination paper. Using questions from past exam papers, the PPT with audio help and audio script in the notes will walk you through a mock examination paper, helping you revise and practise useful exam techniques. You can complete the “mock” in a single sitting, tackle one or two questions at a time or revisit some parts of the presentation to reinforce your learning. Make sure you download the PPT to ensure the audio function works effectively, make sure your speakers / headphones are on and don’t forget to ask your teacher if there is anything you are unsure of.";
      case "OR":
        return "";
      default:
        return "";
    }
  } else if (locale === "cy") {
    switch (resourceType) {
      case "KO":
        return (
          "Dyma gasgliad o enghreifftiau o drefnyddion gwybodaeth i gefnogi'r dysgu ar gyfer " +
          title +
          ". Gall y rhain gael eu defnyddio ar gyfer adolygu neu fel man cychwyn er mwyn creu eich trefnyddion gwybodaeth eich hunain."
        );
      case "SP":
        return "Cynlluniwr y gallwch ei lawrlwytho i gynorthwyo dysgwyr i ddilyn eu llwybr dysgu o'r pynciau sy'n cael eu hasesu. Bydd hyn yn adnodd hynod werthfawr i gynorthwyo adolygu trwy roi trosolwg sydyn o'r pynciau y mae angen i ddysgwyr ailymweld â nhw.";
      case "CR":
        return "";
      case "DT":
        return "";
      case "QB":
        return "Adeiladwch eich papurau eich hun er mwyn profi eich dealltwriaeth o bynciau penodol. Mae’r banc cwestiynau hefyd yn cynnwys cynlluniau marcio fel eich bod yn gallu marcio eich gwaith eich hun a gweld eich cynnydd.";
      case "OER":
        return "Edrychwch ar atebion i gwestiynau hen bapurau ac ystyriwch sylwadau’r arholwr. Bydd yr adnoddau yma yn eich helpu chi i ddeall am beth mae’r arholwr yn chwilio yn eich atebion ac i’ch helpu i osgoi camgymeriadau cyffredin.";
      case "EWT":
        return "Wedi'i anelu at ddysgwyr, mae'r adnodd Arweiniad i’r Arholiad hwn yn cynnig awgrymiadau ymarferol ar sut i fynd i'r afael â chwestiynau yn y papur arholiad yn effeithiol. Gan ddefnyddio cwestiynau o chyn bapurau arholiad, bydd y PPT, gyda chymorth sain a sgript sain yn y nodiadau, yn eich tywys trwy ffug bapur arholiad, gan eich helpu i adolygu ac ymarfer technegau arholiad defnyddiol. Gallwch gwblhau'r “ffug arholiad” i gyd ar unwaith, mynd i’r afael ag un neu ddau o gwestiynau ar y tro neu ailedrych ar rai rhannau o'r cyflwyniad i atgyfnerthu'ch dysgu. Gwnewch yn siŵr eich bod yn llwytho’r PPT i lawr i sicrhau bod y sain yn gweithio'n effeithiol, gwnewch yn siŵr bod eich seinyddion / clustffonau ymlaen a pheidiwch ag anghofio gofyn i'ch athro os byddwch chi'n ansicr o unrhyw beth.";
      case "OR":
        return "";
      default:
        return "";
    }
  }

  return "";
};

const UploadOther: React.FC = () => {
  const locale = useLocale();
  const { t: _ } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [createResourceFormData, setCreateResourceFormData] =
    useState<ResourceForm>();
  const [resourceLinksData, setResourceLinksData] = useState<ResourceLink[]>();
  useState<string[]>();
  const [stage, setStage] = useState<stageEnum>(stageEnum.StageOne); // used to control which form is shown
  const [error, setError] = useState<string>();
  const [resourceURL, setResourceURL] = useState<string>(""); // used to display the link for the final resource
  const [resourceType] = useState<ResourceType>(
    (searchParams.get("type") as ResourceType) || "",
  );
  const [createResourceRef] = useCreateResourceReferenceMutation();

  useEffect(() => {
    const resourceTypes = [
      "BL",
      "KO",
      "SP",
      "CR",
      "DT",
      "QB",
      "OER",
      "EWT",
      "OR",
    ];
    if (!resourceTypes.includes(resourceType)) {
      navigate("/select-resource");
    }
  }, [navigate, resourceType]);

  //   const handleShowData = () => {
  //     setResourceData({
  //       createResourceFormData,
  //       resourceLinks: resourceLinksData
  //     });
  //   };

  const handleCreateResource = async () => {
    if (!createResourceFormData) {
      console.error("no form data");
      return;
    }

    const defaultDescription = getDefaultResourceDescription(
      locale,
      resourceType,
      createResourceFormData.resourceTitle[locale] === ""
        ? "[ENTER TITLE HERE]"
        : createResourceFormData.resourceTitle[locale],
    );
    const subjectId = createResourceFormData.subject?.id ?? "";
    const pk = `REF#SUBJECT:${subjectId}#${locale}`.toUpperCase();
    const sk =
      `QUALIFICATION:${createResourceFormData.qualification}#TYPE:${resourceType}#CODE:${createResourceFormData?.code}`.toUpperCase();
    const obj: ResourceReference = {
      Code: createResourceFormData.code,
      SpecItem: createResourceFormData.specItem,
      Branding: createResourceFormData.branding,
      Object: {
        title: createResourceFormData.resourceTitle[locale],
        specItems: [],
        acknowledgements: createResourceFormData.acknowledgements,
        tags: [],
        links: resourceLinksData ?? [],
        levels: createResourceFormData.levels,
        authors: createResourceFormData.author,
        fundedBy: createResourceFormData.fundedBy,
        description: defaultDescription,
        synopsis: defaultDescription.substring(
          0,
          defaultDescription.indexOf("."),
        ), // Use the first sentence of the description
      },
      Qualification: createResourceFormData.qualification as QualificationId,
      Status: "draft",
      Type: resourceType,
      Subject: createResourceFormData.subject as Subject,
      Keystage: 4,
      SK: sk,
      Locale: locale,
      PK: pk,
    };

    await createResourceRef({ resourceReferenceItem: obj })
      .then(() => {
        setStage(stageEnum.StageFour);
        setResourceURL(
          `/${createResourceFormData?.subject?.title[locale]}/r/${createResourceFormData.code}/${resourceType}/${createResourceFormData.qualification.toUpperCase()}`,
        );
      })
      .catch((error) => {
        //TODO: this doesnt actually catch request errors
        setError(`Failed to create resource: ${error.data.message}`);
        return;
      });
  };
  return (
    <AuthFlag authGroup={"staff"} fallback={<h1>{_("Unauthorised")}</h1>}>
      <div className="d-flex flex-column gap-4">
        {error ? (
          <>
            <Alert variant="danger">
              {/* Should be translated, but hard to do without defined options for the error. Maybe error should be an enum to define its potential states? - Dylan */}
              <ExclamationTriangle className="text-danger" /> {error}
            </Alert>
            <Button
              onClick={() => {
                setError(undefined);
              }}
            >
              {_("Retry")}
            </Button>
          </>
        ) : (
          <>
            {stage === stageEnum.StageOne && (
              <CreateResourceForm
                propDefaultResourceForm={createResourceFormData} // uses submitted form data so next stage can have back button
                buttonText={_("Next")}
                uploadFinal={(formData: ResourceForm) => {
                  setCreateResourceFormData(formData);
                  setStage(stageEnum.StageTwo);
                }}
              />
            )}

            {stage === stageEnum.StageTwo && (
              <>
                <Button onClick={() => setStage(stageEnum.StageOne)}>
                  {_("Back")}
                </Button>

                <ResourceLinks
                  buttonText={_("Next")}
                  propDefaultResourceLinks={resourceLinksData}
                  saveLinks={(links) => {
                    setResourceLinksData(links);
                    setStage(stageEnum.StageThree);
                  }}
                />
              </>
            )}

            {stage === stageEnum.StageThree && (
              <>
                <Button onClick={() => setStage(stageEnum.StageTwo)}>
                  {_("Back")}
                </Button>
                <Button onClick={() => handleCreateResource()}>
                  {_("Create Resource")}
                </Button>
              </>
            )}

            {stage === stageEnum.StageFour && (
              <>
                <h3>{_("Resource Created")}</h3>
                {resourceURL !== "" && (
                  <Link className="btn btn-primary" to={resourceURL}>
                    {_("View resource")}
                  </Link>
                )}
              </>
            )}
          </>
        )}
      </div>
    </AuthFlag>
  );
};

export default UploadOther;
